'use client';

import {
  ANIMATION_CONFIG,
  COOKIE_MAX_AGE,
  DEFAULT_PRIZES,
  EMAIL_REGEX,
  ERROR_MESSAGES,
  SPINNING_WHEEL_COOKIE,
  type ClaimResult,
  type Prize,
  type SpinResult
} from '@/lib/spinning-wheel/constants';
import { X } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';

interface SpinRecord {
  id: string;
  userEmail: string;
  prizeId: string;
  prizeName: string;
  timestamp: string;
}

export function SpinningWheelToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [prizes, setPrizes] = useState<Prize[]>(DEFAULT_PRIZES);
  
  // Spinning states
  const [isSpinning, setIsSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const [wonPrize, setWonPrize] = useState<Prize | null>(null);
  const [rotation, setRotation] = useState(0);
  
  // Claiming states
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<ClaimResult | null>(null);

  // Debug mode check
  const isDebugMode = process.env.NEXT_PUBLIC_IS_DEBUG === 'true';

  useEffect(() => {
    // Show modal if user hasn't seen it and screen is large enough
    if (window.innerHeight >= 650 && !document.cookie.includes(SPINNING_WHEEL_COOKIE)) {
      setIsVisible(true);
      loadPrizeConfig();
    }
  }, []);

  const loadPrizeConfig = async () => {
    try {
      const response = await fetch('/api/spinning-wheel/config');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.prizes) {
          setPrizes(data.prizes);
        }
      }
    } catch (error) {
      console.error('Failed to load prize configuration:', error);
    }
  };

  const spinWheel = async () => {
    if (isSpinning || hasSpun) return;

    setIsSpinning(true);
    
    try {
      const response = await fetch('/api/spinning-wheel/spin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result: SpinResult = await response.json();
      
      if (result.success && result.prize) {
        // Always use ALL prizes for display (including exhausted ones for marketing)
        const allPrizes = prizes; // Show all prizes on wheel
        const prizeIndex = allPrizes.findIndex(p => p.id === result.prize!.id);
        const segmentAngle = 360 / allPrizes.length;

        // Angle (in deg) to the center of the prize segment in the wheel's initial orientation
        const centerAngle = prizeIndex * segmentAngle + segmentAngle / 2;
        
        // We want the center of the prize segment to align with the pointer (top).
        // As derived earlier, the pointer corresponds to an angle of 180deg in our coordinate system.
        const pointerAngle = 0;
        // Calculate shortest positive rotation needed to bring centerAngle to pointerAngle
        let rotationOffset = (360 + pointerAngle - centerAngle) % 360;
        
        // Ensure we always complete at least the minimum number of full rotations for effect
        const baseRotations = ANIMATION_CONFIG.MIN_ROTATION; // 5 full spins = 1800deg
        const finalRotation = rotation + baseRotations + rotationOffset;
        setRotation(finalRotation);
        
        // Show result after animation completes
        setTimeout(() => {
          setWonPrize(result.prize!);
          setHasSpun(true);
          setIsSpinning(false);
        }, ANIMATION_CONFIG.SPIN_DURATION);
      } else {
        setIsSpinning(false);
        alert(result.message || ERROR_MESSAGES.GENERIC_ERROR);
      }
    } catch (error) {
      setIsSpinning(false);
      console.error('Spin failed:', error);
      alert(ERROR_MESSAGES.NETWORK_ERROR);
    }
  };

  const claimPrize = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !wonPrize) return;

    // Validate email using shared regex
    if (!EMAIL_REGEX.test(email.trim())) {
      setClaimResult({
        success: false,
        message: ERROR_MESSAGES.INVALID_EMAIL
      });
      return;
    }

    setIsClaiming(true);
    setClaimResult(null);

    try {
      const response = await fetch('/api/spinning-wheel/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.trim(),
          prizeId: wonPrize.id,
          prizeName: wonPrize.displayName
        })
      });

      const result: ClaimResult = await response.json();
      setClaimResult(result);

      if (result.success) {
        // Auto-close after successful claim
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else if (result.needNewEmail) {
        // Clear email so user can enter a new one
        setEmail('');
      }
    } catch (error) {
      setClaimResult({
        success: false,
        message: ERROR_MESSAGES.NETWORK_ERROR
      });
    } finally {
      setIsClaiming(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    document.cookie = `${SPINNING_WHEEL_COOKIE}; max-age=${COOKIE_MAX_AGE}; path=/`;
  };

  if (!isVisible) return null;

  // Always show all prizes for marketing purposes (including exhausted ones)
  const allPrizesForDisplay = prizes;

  const WheelComponent = () => (
    <div className="relative w-80 h-80 sm:w-96 sm:h-96 lg:w-110 lg:h-110 mx-auto mb-4 sm:mb-6 lg:mb-8">
      {/* Rotating container */}
      <div 
        className="relative w-full h-full rounded-full shadow-2xl overflow-hidden"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: `transform ${ANIMATION_CONFIG.SPIN_DURATION}ms ease-out`
        }}
      >
        {/* Bottom layer - SVG sectors */}
        <svg
          className="absolute inset-0 w-full h-full z-10"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          {allPrizesForDisplay.map((prize, index) => {
            const segmentAngle = 360 / allPrizesForDisplay.length;
            const startAngle = index * segmentAngle;
            const endAngle = (index + 1) * segmentAngle;
            
            // Alternate between the two colors, but dim exhausted prizes only in debug mode
            let segmentColor = index % 2 === 0 ? '#14BBD3' : '#F9FAE8';
            
            // Only dim exhausted prizes in debug mode
            if (isDebugMode && prize.remaining <= 0) {
              segmentColor = index % 2 === 0 ? '#0A8A99' : '#E8E8D8'; // Slightly darker versions
            }
            
            // Convert angles to radians
            const startAngleRad = (startAngle - 90) * Math.PI / 180;
            const endAngleRad = (endAngle - 90) * Math.PI / 180;
            
            // Calculate path points for curved segments
            const radius = 90;
            const centerX = 100;
            const centerY = 100;
            
            const x1 = centerX + radius * Math.cos(startAngleRad);
            const y1 = centerY + radius * Math.sin(startAngleRad);
            const x2 = centerX + radius * Math.cos(endAngleRad);
            const y2 = centerY + radius * Math.sin(endAngleRad);
            
            const largeArcFlag = segmentAngle > 180 ? 1 : 0;
            
            const pathData = [
              `M ${centerX} ${centerY}`,
              `L ${x1} ${y1}`,
              `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
              'Z'
            ].join(' ');
            
            return (
              <path
                key={prize.id}
                d={pathData}
                fill={segmentColor}
                stroke="none"
              />
            );
          })}
        </svg>
        
        {/* Middle layer - Background image */}
        <div 
          className="absolute inset-0 w-full h-full rounded-full z-20"
          style={{ 
            backgroundImage: 'url(/images/common/spin-wheel-base.png)',
            backgroundSize: '130%',
            backgroundPosition: '50% 45%',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        {/* Prize content overlay */}
        {allPrizesForDisplay.map((prize, index) => {
          const segmentAngle = 360 / allPrizesForDisplay.length;
          const startAngle = index * segmentAngle;
          const textColor = index % 2 === 0 ? 'white' : '#00529F';
          
          // Only dim text for exhausted prizes in debug mode
          const finalTextColor = (isDebugMode && prize.remaining <= 0)
            ? (index % 2 === 0 ? '#CCCCCC' : '#666666')
            : textColor;
          
          // Calculate the position for the content - place it at the middle of the segment
          const textAngle = startAngle + segmentAngle / 2;
          const textRadius = 110; // Distance from center 
          const textX = 50 + (textRadius * 0.25) * Math.cos((textAngle - 90) * Math.PI / 180);
          const textY = 50 + (textRadius * 0.25) * Math.sin((textAngle - 90) * Math.PI / 180);
          
          return (
            <div
              key={`content-${prize.id}`}
              className="absolute flex flex-col items-center justify-center font-bold z-30"
              style={{
                left: `${textX}%`,
                top: `${textY}%`,
                transform: `translate(-50%, -50%) rotate(${textAngle}deg)`,
                width: '80px',
                height: '80px',
                color: finalTextColor,
                opacity: (isDebugMode && prize.remaining <= 0) ? 0.6 : 1 // Only fade in debug mode
              }}
            >
              {prize.image ? (
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 mb-1 sm:mb-2 lg:mb-3 relative flex-shrink-0">
                  <Image
                    src={prize.image}
                    alt={prize.name}
                    fill
                    className="object-contain"
                    style={{ opacity: (isDebugMode && prize.remaining <= 0) ? 0.6 : 1 }}
                    unoptimized={process.env.NODE_ENV === 'development'}
                    priority
                  />
                </div>
              ) : (
                <div className="text-sm sm:text-base lg:text-lg mb-1 sm:mb-2 lg:mb-3 flex-shrink-0">{prize.icon}</div>
              )}
              {/* Text container with 90deg rotation */}
              <div style={{ transform: 'rotate(90deg)', width: '60px' }} className="flex-1 flex flex-col justify-center sm:w-70px lg:w-80px">
                {/* Title (larger) */}
                <div className="text-center leading-tight text-[10px] sm:text-xs lg:text-sm font-bold mb-0.5 overflow-hidden">
                  <div>{prize.name}</div>
                </div>
                {/* Description (smaller) */}
                {prize.description && (
                  <div className="text-center leading-tight text-[7px] sm:text-[8px] lg:text-[10px] font-normal opacity-90 overflow-hidden">
                    <div>{prize.description}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Center and Pointer - Combined image */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-40">
        <img
src="/images/common/center-pointer.webp"
          alt="Center pointer"
          className="w-24 h-24 sm:w-32 sm:h-32 lg:w-[150px] lg:h-[150px] object-contain"
        />
      </div>
    </div>
  );

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={handleClose}
    >
      <div 
        className="relative bg-gradient-to-br from-[#F8F9FA] to-[#F2F9FF] rounded-lg w-full max-w-[350px] sm:max-w-[600px] lg:max-w-[898px] max-h-[95vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col lg:flex-row min-h-[500px] lg:min-h-[620px]">
          {/* Left Side - Spinning Wheel */}
          <div className="w-full lg:w-[450px] h-[400px] sm:h-[400px] lg:h-[670px] relative bg-gradient-to-br from-[#14BBD3] to-[#263AEA] flex-shrink-0 flex items-center justify-center">
            {!hasSpun ? (
              <div className="text-center text-white p-4 sm:p-6 lg:p-8">
                <WheelComponent />
                <button
                  onClick={spinWheel}
                  disabled={isSpinning}
                  className="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-white font-bold py-3 px-6 sm:py-4 sm:px-8 rounded-full text-lg sm:text-xl shadow-lg transition-all transform hover:scale-105 disabled:hover:scale-100"
                >
                  {isSpinning ? 'Spinning...' : 'SPIN TO WIN!'}
                </button>
              </div>
            ) : (
              <div className="text-center text-white p-4 sm:p-6 lg:p-8">
                <div className="text-4xl sm:text-5xl lg:text-6xl mb-4">{wonPrize?.icon}</div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2">Congratulations!</h3>
                <p className="text-base sm:text-lg">You won: {wonPrize?.displayName}</p>
              </div>
            )}
          </div>

          {/* Right Side - Content */}
          <div className="flex-1 flex flex-col">
            {/* Close Button */}
            <div className="flex justify-end p-3 sm:p-4 lg:p-6">
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/50 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <X size={18} className="sm:w-5 sm:h-5 text-[#73777F]" strokeWidth={2} />
              </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-12 pb-6 sm:pb-8">
              {/* Logo */}
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <Image
                  src="/icons/logo.png"
                  alt="Pion Power"
                  width={140}
                  height={15}
                  className="h-3 sm:h-4 lg:h-6 w-auto"
                />
              </div>

              {!hasSpun ? (
                <>
                  {/* Pre-spin Content */}
                  {/* Canada Day Sale Header */}
                  <div className="mb-0 sm:mb-0 lg:mb-0">

                    {/* Date Section */}
                    <div className="text-center mb-6">
                      <p className="text-sm leading-4 text-[#495057] font-medium">
                        Starts on June 27th
                      </p>
                    </div>

                    {/* Canada Day Banner */}
                    <div className="flex justify-center mb-8">
                      <Image
src="/images/spinning-wheel/canada-day-banner.webp"
                        alt="Canada Day Sale"
                        width={393}
                        height={80}
                        className="w-full h-auto max-w-full"
                      />
                    </div>

                    {/* Discount Section */}
                    <div className="flex flex-col items-center gap-2 sm:gap-3 lg:gap-4 w-full max-w-[229px] mx-auto mb-6">
                      <div className="text-center">
                        <p className="text-[24px] sm:text-[30px] lg:text-[36px] leading-[28px] sm:leading-[36px] lg:leading-[43px] text-[#F72121] font-futura-heavy">
                          Up to
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-[36px] sm:text-[44px] lg:text-[53px] leading-[40px] sm:leading-[50px] lg:leading-[58px] text-[#F72121] font-futura-heavy">
                          43% OFF
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <h1 className="text-[20px] sm:text-[28px] lg:text-[30px] leading-[24px] sm:leading-[34px] lg:leading-[43px] font-bold text-[#00529F] font-futura mb-3 sm:mb-4 lg:mb-6">
                    SPIN TO WIN
                    EXTRA GIFT OR
                    DISCOUNT
                  </h1>
                  <p className="text-[14px] sm:text-[16px] lg:text-[20px] leading-[16px] sm:leading-[20px] lg:leading-[24px] text-[#73777F] font-futura-md max-w-sm mb-4 sm:mb-6 lg:mb-8 px-2">
                    Try your luck for a chance to win exclusive rewards, just in time for the Canada Day long weekend!
                  </p>
                </>
              ) : (
                <>
                  {/* Post-spin Content */}
                  <h1 className="text-[20px] sm:text-[28px] lg:text-[36px] leading-[24px] sm:leading-[34px] lg:leading-[43px] font-bold text-[#00529F] font-futura mb-3 sm:mb-4 lg:mb-6">
                    CLAIM YOUR<br />
                    PRIZE!
                  </h1>
                  <p className="text-[14px] sm:text-[16px] lg:text-[18px] leading-[16px] sm:leading-[20px] lg:leading-[22px] text-[#73777F] font-futura-md max-w-sm mb-4 sm:mb-6 lg:mb-8 px-2">
                    Enter your email to receive your prize details and unique coupon code.
                  </p>

                  {/* Email Form */}
                  <form onSubmit={claimPrize} className="w-full max-w-sm mb-4 lg:mb-6">
                    <div className="mb-3">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-12 lg:h-14 px-4 border border-[#00529F] rounded-md text-[16px] text-[#00529F] placeholder-[#00529F] font-futura-md focus:outline-none focus:ring-2 focus:ring-[#00529F] focus:ring-opacity-50 transition-all"
                        required
                        disabled={isClaiming}
                      />
                    </div>
                    <div>
                      <button
                        type="submit"
                        disabled={isClaiming || !email.trim()}
                        className="w-full h-12 lg:h-14 px-4 lg:px-6 bg-[#00529F] text-white font-futura-heavy text-[16px] lg:text-[20px] leading-[20px] lg:leading-[28px] rounded-lg hover:bg-[#004080] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isClaiming ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="hidden lg:inline">CLAIMING...</span>
                            <span className="lg:hidden">...</span>
                          </span>
                        ) : (
                          'CLAIM PRIZE'
                        )}
                      </button>
                    </div>
                  </form>

                  {/* Status Messages */}
                  {claimResult && (
                    <div className={`text-center mb-4 ${claimResult.success ? 'text-green-600' : 'text-red-600'}`}>
                      <p className="text-sm">{claimResult.message}</p>
                      {claimResult.success && (
                        <p className="text-xs mt-2 text-[#73777F]">Stay tuned and happy shopping! 🛒</p>
                      )}
                    </div>
                  )}

                  {/* Disclaimer */}
                  <p className="text-[14px] lg:text-[16px] leading-[17px] lg:leading-[19px] text-[#73777F] font-futura-book text-center max-w-sm mb-6 lg:mb-8 px-2">
                    *By claiming your prize, you agree to receive promotional emails from Pion Power.
                  </p>
                </>
              )}

              {/* Bottom Actions */}
              {!hasSpun && (
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleClose}
                    className="text-[16px] leading-[19px] text-[#73777F] font-futura-md hover:text-[#495057] transition-colors"
                  >
                    No Thanks
                  </button>
                  <div className="w-[5px] h-[10px]">
                    <svg width="5" height="10" viewBox="0 0 5 10" fill="none">
                      <path d="M0 0L5 5L0 10" stroke="#73777F" strokeWidth="2" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 